import { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Sprout, X, Plus } from 'lucide-react';
import nurseryService from '../../services/nursery';
import TreeTypeGradesEditor from '../../components/nursery/TreeTypeGradesEditor';
import GradeDiscountsCard from '../../components/nursery/GradeDiscountsCard';
import { gradesPayload, gradesProblem, sameGrades, type GradeDraft } from '../../utils/treeTypeGrades';
import type {
  NurseryTreeType, ReadinessMode, LeadTimeAnchor, TreeTypeUpdatePayload, CreateTreeTypePayload,
  TreeTypeGrade, GradeDiscount, TreeTypePurpose,
} from '../../types/nursery';

const EMPTY_CREATE: CreateTreeTypePayload = { code: '', name: '', stock_type: 'B', description: '', sort_order: 0, purpose: 'sale' };

/**
 * Sale or propagation. A propagation tree type (a 35 mm cell, a liner, a rooted
 * layer) is grown on into another; its new stock items start not for sale.
 */
function PurposeField({ value, disabled, onChange }: {
  value: TreeTypePurpose; disabled?: boolean; onChange: (v: TreeTypePurpose) => void;
}) {
  return (
    <div>
      <label htmlFor="tree-type-purpose" className="block text-sm font-medium text-neutral-700 mb-1">Used for</label>
      <select
        id="tree-type-purpose"
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value as TreeTypePurpose)}
        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
      >
        <option value="sale">Selling</option>
        <option value="propagation">Propagation (grown on into another tree type)</option>
      </select>
      {value === 'propagation' && (
        <p className="text-xs text-neutral-500 mt-1">
          New stock items of this tree type start as not for sale. Tick &ldquo;Offered for sale&rdquo; on one to sell it.
        </p>
      )}
    </div>
  );
}

const MODE_LABEL: Record<ReadinessMode, string> = {
  lead_time: 'Lead time (offset from a date)',
  seasonal_window: 'Seasonal window (dig/ship season)',
  manual: 'Manual (set per lot)',
};

const ANCHOR_LABEL: Record<LeadTimeAnchor, string> = {
  production_date: 'Production date',
  pot_date: 'Pot date',
  plant_date: 'Plant date',
  graft_date: 'Graft date',
  bud_date: 'Bud date',
  last_movement: 'Last movement',
};

/** A loss % as a number, whether the server sent it as a string or not. */
const pct = (v: unknown, fallback: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function readinessSummary(t: NurseryTreeType): string {
  if (t.readiness_mode === 'lead_time') {
    return t.lead_time_days != null
      ? `${t.lead_time_days} days after ${ANCHOR_LABEL[t.lead_time_anchor].toLowerCase()}`
      : 'Lead time (not set)';
  }
  if (t.readiness_mode === 'seasonal_window') {
    if (t.season_start_month) {
      const end = t.season_end_month || t.season_start_month;
      const grow = t.min_growing_months ? `, after ${t.min_growing_months} months growing` : '';
      return `Season ${MONTHS[t.season_start_month - 1]}–${MONTHS[end - 1]}${grow}`;
    }
    return 'Seasonal window (not set)';
  }
  return 'Manual';
}

function TreeTypeAdmin() {
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<NurseryTreeType | null>(null);
  const [form, setForm] = useState<TreeTypeUpdatePayload>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [grades, setGrades] = useState<TreeTypeGrade[]>([]);
  const [discounts, setDiscounts] = useState<GradeDiscount[]>([]);
  const [gradeRows, setGradeRows] = useState<GradeDraft[]>([]);

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTreeTypePayload>(EMPTY_CREATE);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, gradeRes] = await Promise.all([
        nurseryService.getTreeTypes(),
        // Grades are extra: an older server without them still lists tree types.
        nurseryService.getTreeTypeGrades().catch(() => null),
      ]);
      if (res.success) setTreeTypes(res.data || []);
      else setError('Failed to load tree types');
      if (gradeRes?.success) {
        setGrades(gradeRes.data.grades);
        setDiscounts(gradeRes.data.discounts);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tree types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (t: NurseryTreeType) => {
    setEditing(t);
    setForm({
      name: t.name,
      description: t.description,
      sort_order: t.sort_order,
      readiness_mode: t.readiness_mode,
      lead_time_days: t.lead_time_days,
      lead_time_anchor: t.lead_time_anchor,
      season_start_month: t.season_start_month,
      season_end_month: t.season_end_month,
      min_growing_months: t.min_growing_months,
      loss_pct_bud: pct(t.loss_pct_bud, 30),
      loss_pct_first: pct(t.loss_pct_first, 15),
      loss_pct_second: pct(t.loss_pct_second, 5),
      purpose: t.purpose ?? 'sale',
    });
    setGradeRows(gradesOf(t.id).map((g) => ({ name: g.name, description: g.description, expected_share_pct: g.expected_share_pct })));
    setSaveError(null);
  };

  const gradesOf = (treeTypeId: number) => grades.filter((g) => g.tree_type_id === treeTypeId);

  const openCreate = () => {
    setCreateForm(EMPTY_CREATE);
    setCreateError(null);
    setCreating(true);
  };

  const create = async () => {
    if (!createForm.code.trim() || !createForm.name.trim()) {
      setCreateError('Code and name are required');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const res = await nurseryService.createTreeType({
        code: createForm.code.trim(),
        name: createForm.name.trim(),
        stock_type: createForm.stock_type,
        description: createForm.description?.trim() || undefined,
        sort_order: createForm.sort_order,
        purpose: createForm.purpose,
      });
      if (res.success) {
        setCreating(false);
        load();
      } else {
        setCreateError((res as { error?: { message?: string } }).error?.message || 'Failed to create');
      }
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setCreateSaving(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    const problem = gradesProblem(gradeRows);
    if (problem) { setSaveError(problem); return; }
    setSaving(true);
    setSaveError(null);
    try {
      // A built-in tree type's rules are shared; its grades are this organisation's own.
      if (!isGlobal) {
        const res = await nurseryService.updateTreeType(editing.id, form);
        if (!res.success) {
          setSaveError((res as { error?: { message?: string } }).error?.message || 'Failed to save');
          return;
        }
      }
      const before = gradesOf(editing.id).map((g) => ({ name: g.name, description: g.description, expected_share_pct: g.expected_share_pct }));
      if (!sameGrades(before, gradeRows)) {
        const res = await nurseryService.setTreeTypeGrades(editing.id, gradesPayload(gradeRows));
        if (!res.success) {
          setSaveError((res as { error?: { message?: string } }).error?.message || 'Failed to save the grades');
          return;
        }
      }
      setEditing(null);
      load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSaving(false);
    }
  };

  const isGlobal = editing?.organisation_id == null;

  return (
    <>
      <PageHeader
        title="Tree Types & Readiness Rules"
        subtitle="How a lot's ready date is calculated for each tree type"
        icon={<Sprout className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> New tree type
          </Button>
        }
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {!loading && (
          <GradeDiscountsCard
            maxRank={Math.max(0, ...grades.map((g) => g.grade_rank))}
            discounts={discounts}
            onSaved={setDiscounts}
          />
        )}

        {loading ? (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : treeTypes.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-xl px-6 py-12 text-center">
            <Sprout className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
            <p className="text-sm text-neutral-600 mb-1">No tree types yet</p>
            <p className="text-xs text-neutral-400 mb-4">Create the tree types your nursery uses (e.g. bareroot grades and container sizes).</p>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> New tree type
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Readiness</th>
                  <th className="px-4 py-2 font-medium">Grades</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {treeTypes.map(t => (
                  <tr key={t.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-2 font-mono text-neutral-700">{t.code}</td>
                    <td className="px-4 py-2 text-neutral-800">{t.name}</td>
                    <td className="px-4 py-2">
                      {t.stock_type === 'B' ? 'Bareroot' : 'Container'}
                      {t.purpose === 'propagation' && (
                        <span className="ml-2 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Propagation</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-neutral-600">{readinessSummary(t)}</td>
                    <td className="px-4 py-2 text-neutral-600">
                      {gradesOf(t.id).length ? gradesOf(t.id).map((g) => g.name).join(', ') : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="outline" size="sm" onClick={() => openEdit(t)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-neutral-800">Edit {editing.code}</h3>
                <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
              </div>

              {isGlobal && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 mb-4">
                  This is a built-in tree type: its readiness rules and losses can&apos;t be changed here. Create a new tree type if you need your own. Its grades are yours to set.
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={form.name ?? ''}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={form.description ?? ''}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Sort order</label>
                  <input
                    type="number"
                    value={form.sort_order ?? 0}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, sort_order: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                </div>

                <PurposeField
                  value={form.purpose ?? 'sale'}
                  disabled={isGlobal}
                  onChange={purpose => setForm(f => ({ ...f, purpose }))}
                />

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Readiness mode</label>
                  <select
                    value={form.readiness_mode}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, readiness_mode: e.target.value as ReadinessMode }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  >
                    {(Object.keys(MODE_LABEL) as ReadinessMode[]).map(m => (
                      <option key={m} value={m}>{MODE_LABEL[m]}</option>
                    ))}
                  </select>
                </div>

                {form.readiness_mode === 'lead_time' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Lead time (days)</label>
                      <input
                        type="number" min="0"
                        value={form.lead_time_days ?? ''}
                        onChange={e => setForm(f => ({ ...f, lead_time_days: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Anchor date</label>
                      <select
                        value={form.lead_time_anchor}
                        onChange={e => setForm(f => ({ ...f, lead_time_anchor: e.target.value as LeadTimeAnchor }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      >
                        {(Object.keys(ANCHOR_LABEL) as LeadTimeAnchor[]).map(a => (
                          <option key={a} value={a}>{ANCHOR_LABEL[a]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {form.readiness_mode === 'lead_time' && (
                  <p className="text-xs text-neutral-500 -mt-2">
                    Production date is the latest of when the trees were sown, budded, grafted or potted.
                  </p>
                )}

                {form.readiness_mode === 'seasonal_window' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Season start</label>
                      <select
                        value={form.season_start_month ?? ''}
                        onChange={e => setForm(f => ({ ...f, season_start_month: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">—</option>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Season end</label>
                      <select
                        value={form.season_end_month ?? ''}
                        onChange={e => setForm(f => ({ ...f, season_end_month: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">—</option>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label htmlFor="tt-min-growing" className="block text-sm font-medium text-neutral-700 mb-1">Months growing before the first season</label>
                      <input
                        id="tt-min-growing"
                        type="number" min="0" max="60"
                        value={form.min_growing_months ?? ''}
                        disabled={isGlobal}
                        onChange={e => setForm(f => ({ ...f, min_growing_months: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Counted from when the trees were produced (sown, budded, grafted or potted). Ready at the first season start after that.
                        A summer bud that grows a whole season first might be 15.
                      </p>
                    </div>
                  </div>
                )}

                <fieldset className="border border-neutral-200 rounded-lg p-3">
                  <legend className="text-sm font-medium text-neutral-700 px-1">Expected losses before sale</legend>
                  <p className="text-xs text-neutral-500 mb-2">
                    Taken off a lot&apos;s count in the forecast, by the best count it has had.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {([['loss_pct_bud', 'After bud count'], ['loss_pct_first', 'After 1st count'], ['loss_pct_second', 'After 2nd count']] as const).map(([key, label]) => (
                      <label key={key} className="text-xs text-neutral-600 min-w-0">
                        {label}
                        <span className="mt-0.5 flex items-center gap-1">
                          <input
                            type="number" min="0" max="99"
                            value={form[key] ?? ''}
                            disabled={isGlobal}
                            onChange={e => setForm(f => ({ ...f, [key]: e.target.value === '' ? 0 : Number(e.target.value) }))}
                            className="w-full min-w-0 border border-neutral-300 rounded-lg px-2 py-1.5 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                          />
                          <span>%</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="border border-neutral-200 rounded-lg p-3">
                  <legend className="text-sm font-medium text-neutral-700 px-1">Grades</legend>
                  <p className="text-xs text-neutral-500 mb-2">
                    Best first. The expected share is how much of a crop is usually each grade; the forecast splits a growing crop by it until it is graded.
                  </p>
                  <TreeTypeGradesEditor rows={gradeRows} onChange={setGradeRows} />
                </fieldset>

                {saveError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{saveError}</div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {creating && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            {/* Scrolls inside a short screen, so its buttons are always reachable. */}
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-full overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-neutral-800">New tree type</h3>
                <button onClick={() => setCreating(false)}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Code</label>
                    <input
                      type="text" maxLength={20}
                      value={createForm.code}
                      onChange={e => setCreateForm(f => ({ ...f, code: e.target.value }))}
                      placeholder="e.g. P10L"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Stock type</label>
                    <select
                      value={createForm.stock_type}
                      onChange={e => setCreateForm(f => ({ ...f, stock_type: e.target.value as 'B' | 'P' }))}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="B">Bareroot</option>
                      <option value="P">Container</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                  <input
                    type="text" maxLength={100}
                    value={createForm.name}
                    onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. 10L container"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={createForm.description ?? ''}
                    onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Sort order</label>
                  <input
                    type="number"
                    value={createForm.sort_order ?? 0}
                    onChange={e => setCreateForm(f => ({ ...f, sort_order: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <PurposeField
                  value={createForm.purpose ?? 'sale'}
                  onChange={purpose => setCreateForm(f => ({ ...f, purpose }))}
                />

                <p className="text-xs text-neutral-500">
                  Set readiness rules (lead time / seasonal window) after creating, via “Edit”.
                </p>

                {createError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{createError}</div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={create} disabled={createSaving}>
                  {createSaving ? 'Creating…' : 'Create tree type'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TreeTypeAdmin;
